import { useRef, useState } from 'react';
import { Button } from '@design-system';
import { File, Trash, Upload } from 'lucide-react';
import { Tooltip } from '@mui/material';
import { toast } from 'sonner';

const PDF_MIME = 'application/pdf';
const PDF_MAX_MB = 30;

interface RateActionUploadFormProps {
  onSubmit: (file: File) => void;
  /** True while the parent's extraction lifecycle is mid-flight; disables interactions. */
  disabled?: boolean;
  /** Optional error to surface in-line (e.g. last extraction failed). */
  errorMessage?: string | null;
}

/**
 * Pure file picker — selects/validates a PDF and hands it to the parent. The parent owns
 * the extraction lifecycle (upload, enqueue, poll) and renders progress in a stable slot
 * so the progress bar isn't unmounted when the URL changes mid-operation.
 */
export function RateActionUploadForm({
  onSubmit,
  disabled = false,
  errorMessage,
}: RateActionUploadFormProps) {
  const [file, setFile] = useState<File | null>(null);

  function handleFileSelected(f: File | null) {
    if (!f) {
      setFile(null);
      return;
    }
    if (f.type !== PDF_MIME) {
      toast.error(
        `${f.type || 'Unknown file type'} is not supported. Please upload a PDF file.`,
      );
      return;
    }
    if (f.size > PDF_MAX_MB * 1024 * 1024) {
      toast.error(`File size exceeds ${PDF_MAX_MB}MB. Please upload a smaller file.`);
      return;
    }
    setFile(f);
  }

  function handleSubmit() {
    if (!file) return;
    onSubmit(file);
  }

  return (
    <div className="flex flex-col gap-4">
      {!file ? (
        <DropZone onFileSelected={handleFileSelected} disabled={disabled} />
      ) : (
        <FilePill
          file={file}
          onRemove={() => handleFileSelected(null)}
          disabled={disabled}
        />
      )}

      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}

      {file && (
        <Button
          className="w-full rounded-full font-medium sm:w-auto sm:self-end sm:px-8"
          onClick={handleSubmit}
          disabled={disabled}
        >
          {errorMessage ? 'Try again' : 'Extract letter'}
        </Button>
      )}
    </div>
  );
}

function DropZone({
  onFileSelected,
  disabled,
}: {
  onFileSelected: (file: File | null) => void;
  disabled: boolean;
}) {
  const inputRef = useRef<HTMLInputElement | null>(null);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0] ?? null;
    onFileSelected(f);
    if (inputRef.current) inputRef.current.value = '';
  }

  function handleDrop(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
    if (disabled) return;
    const f = e.dataTransfer.files?.[0] ?? null;
    onFileSelected(f);
  }

  function handleDragOver(e: React.DragEvent<HTMLDivElement>) {
    e.preventDefault();
  }

  return (
    <div
      className="flex justify-center rounded-lg border border-dashed border-gray-900/25 px-6 py-10"
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      <div className="text-center">
        <Upload aria-hidden="true" className="text-primary-500 mx-auto h-12 w-12" />
        <div className="mt-4 flex text-sm leading-6 text-gray-700">
          <label
            htmlFor="rate-action-upload"
            className="text-primary focus-within:ring-primary hover:text-primary-hover relative cursor-pointer rounded-md bg-white font-semibold focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2"
          >
            <span>Upload a file</span>
            <input
              ref={inputRef}
              onChange={handleChange}
              id="rate-action-upload"
              name="rate-action-upload"
              type="file"
              accept="application/pdf,.pdf"
              disabled={disabled}
              className="sr-only"
            />
          </label>
          <p className="pl-1">or drag and drop</p>
        </div>
        <p className="text-xs leading-5 text-gray-700">PDF up to {PDF_MAX_MB}MB</p>
      </div>
    </div>
  );
}

function FilePill({
  file,
  onRemove,
  disabled,
}: {
  file: File;
  onRemove: () => void;
  disabled: boolean;
}) {
  return (
    <div className="border-primary-200 flex items-center gap-2 rounded-lg border p-5 text-sm text-gray-700">
      <File className="text-primary h-5 w-5" />
      <span className="truncate">{file.name}</span>
      {!disabled && (
        <Tooltip className="ml-auto" title={<span className="text-sm">Remove file</span>}>
          <Button onClick={onRemove} variant="ghost" size="icon-sm" aria-label="Remove file">
            <Trash className="h-4 w-4 text-gray-700" />
          </Button>
        </Tooltip>
      )}
    </div>
  );
}
